import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthContext';

/**
 * Deep-linking: honor a global `?ws=<accountId>` URL param.
 *
 * The active workspace normally lives only in localStorage, so a shared link
 * to a workspace-relative page (/, /projects, /today, /account/*) would render
 * against the RECIPIENT's active workspace — a different view. When `ws` is
 * present and the viewer is a member of that account, we align the active
 * workspace to it; non-members are silently ignored (the backend enforces
 * access regardless — this is a view-determinism aid, not access control).
 *
 * AccountSelector emits `?ws=` on switch so copied URLs carry the workspace.
 * Mounted once inside the Router + AuthProvider (App.tsx); renders nothing.
 */
export const WorkspaceParamSync: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { me, currentAccount, setCurrentAccount } = useAuth();
  const ws = searchParams.get('ws');

  useEffect(() => {
    if (!ws || !me) return;
    if (currentAccount?.id === ws) return;
    const isMember = (me.accounts ?? []).some((a) => a.id === ws);
    if (isMember) setCurrentAccount(ws);
  }, [ws, me, currentAccount, setCurrentAccount]);

  return null;
};

export default WorkspaceParamSync;
